import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { logout } from '@/store/slices/authSlice';
import {
  fetchDeliveryQuoteThunk,
  fetchMediaCartThunk,
  mediaCheckoutThunk,
  removeMediaCartItemThunk,
  updateMediaCartItemQuantityThunk,
} from '@/store/thunks/mediaCartThunk';
import type { DeliveryQuoteBreakdownItem, DeliveryQuoteResponseAddress, MediaCartSummaryItem, MediaCheckoutResult } from '@/types/media';

export interface DeliveryQuoteState {
  currency: string;
  requiresDelivery: boolean;
  deliveryFeeAmount: number;
  deliveryAddress: DeliveryQuoteResponseAddress | null;
  breakdown: DeliveryQuoteBreakdownItem[];
  loading: boolean;
  error: string | null;
}

export interface MediaCartState {
  items: MediaCartSummaryItem[];
  totalAmount: number;
  currency: string;
  sellerCount: number;
  requiresDelivery: boolean;
  loading: boolean;
  removing: string | null;
  updating: string | null;
  error: string | null;
  deliveryQuote: DeliveryQuoteState;
  checkoutLoading: boolean;
  checkoutError: string | null;
  checkoutResult: MediaCheckoutResult | null;
}

const initialDeliveryQuoteState: DeliveryQuoteState = {
  currency: 'NGN',
  requiresDelivery: false,
  deliveryFeeAmount: 0,
  deliveryAddress: null,
  breakdown: [],
  loading: false,
  error: null,
};

const initialState: MediaCartState = {
  items: [],
  totalAmount: 0,
  currency: 'NGN',
  sellerCount: 0,
  requiresDelivery: false,
  loading: false,
  removing: null,
  updating: null,
  error: null,
  deliveryQuote: initialDeliveryQuoteState,
  checkoutLoading: false,
  checkoutError: null,
  checkoutResult: null,
};

export const mediaCartSlice = createSlice({
  name: 'mediaCart',
  initialState,
  reducers: {
    clearMediaCartState: (state) => {
      state.items = [];
      state.totalAmount = 0;
      state.currency = 'NGN';
      state.sellerCount = 0;
      state.requiresDelivery = false;
      state.error = null;
      state.deliveryQuote = initialDeliveryQuoteState;
    },
    clearDeliveryQuote: (state) => {
      state.deliveryQuote = initialDeliveryQuoteState;
    },
    clearMediaCartError: (state) => {
      state.error = null;
    },
    clearCheckoutError: (state) => {
      state.checkoutError = null;
    },
    clearCheckoutResult: (state) => {
      state.checkoutResult = null;
    },
    setMediaCartLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMediaCartThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMediaCartThunk.fulfilled, (state, action) => {
        const { items, totalAmount, currency, sellerCount, requiresDelivery } =
          action.payload.data;
        state.items = items;
        state.totalAmount = totalAmount;
        state.currency = currency;
        state.sellerCount = sellerCount;
        state.requiresDelivery = requiresDelivery;
        state.loading = false;
      })
      .addCase(fetchMediaCartThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message ?? 'Unable to load cart.';
      })

      .addCase(removeMediaCartItemThunk.pending, (state, action) => {
        state.removing = action.meta.arg;
        state.error = null;
      })
      .addCase(removeMediaCartItemThunk.fulfilled, (state, action) => {
        const removedItem = state.items.find((item) => item.cartItemId === action.payload.cartItemId);
        if (removedItem) {
          state.totalAmount = Math.max(0, state.totalAmount - removedItem.lineTotal);
          state.items = state.items.filter(
            (item) => item.cartItemId !== action.payload.cartItemId,
          );
          state.sellerCount = new Set(state.items.map((item) => item.sellerId)).size;
          state.requiresDelivery = state.items.some((item) => item.deliveryRequired);
        }
        state.removing = null;
      })
      .addCase(removeMediaCartItemThunk.rejected, (state, action) => {
        state.removing = null;
        state.error = action.payload?.message ?? 'Unable to remove item from cart.';
      })

      .addCase(updateMediaCartItemQuantityThunk.pending, (state, action) => {
        state.updating = action.meta.arg.cartItemId;
        state.error = null;
      })
      .addCase(updateMediaCartItemQuantityThunk.fulfilled, (state, action) => {
        const { items, totalAmount, currency, sellerCount, requiresDelivery } =
          action.payload.data;
        state.items = items;
        state.totalAmount = totalAmount;
        state.currency = currency;
        state.sellerCount = sellerCount;
        state.requiresDelivery = requiresDelivery;
        state.updating = null;
      })
      .addCase(updateMediaCartItemQuantityThunk.rejected, (state, action) => {
        state.updating = null;
        state.error = action.payload?.message ?? 'Unable to update cart item.';
      })

      .addCase(fetchDeliveryQuoteThunk.pending, (state) => {
        state.deliveryQuote.loading = true;
        state.deliveryQuote.error = null;
      })
      .addCase(fetchDeliveryQuoteThunk.fulfilled, (state, action) => {
        const { currency, requiresDelivery, deliveryFeeAmount, deliveryAddress, breakdown } = action.payload.data;
        state.deliveryQuote.currency = currency;
        state.deliveryQuote.requiresDelivery = requiresDelivery;
        state.deliveryQuote.deliveryFeeAmount = deliveryFeeAmount;
        state.deliveryQuote.deliveryAddress = deliveryAddress ?? null;
        state.deliveryQuote.breakdown = breakdown ?? [];
        state.deliveryQuote.loading = false;
      })
      .addCase(fetchDeliveryQuoteThunk.rejected, (state, action) => {
        state.deliveryQuote.loading = false;
        state.deliveryQuote.error = action.payload?.message ?? 'Unable to fetch delivery quote.';
      })

      .addCase(mediaCheckoutThunk.pending, (state) => {
        state.checkoutLoading = true;
        state.checkoutError = null;
      })
      .addCase(mediaCheckoutThunk.fulfilled, (state, action) => {
        state.checkoutLoading = false;
        state.checkoutResult = action.payload.data;
      })
      .addCase(mediaCheckoutThunk.rejected, (state, action) => {
        state.checkoutLoading = false;
        state.checkoutError = action.payload?.message ?? 'Unable to process checkout.';
      })

      .addCase(logout, () => initialState);
  },
});

export const { clearMediaCartState, clearMediaCartError, clearDeliveryQuote, clearCheckoutError, clearCheckoutResult, setMediaCartLoading } =
  mediaCartSlice.actions;
export const mediaCartReducer = mediaCartSlice.reducer;